import { useEffect, useState } from 'react';
import { Button, Message, Modal, Typography } from '@arco-design/web-react';
import { IconInfoCircle } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import QRCode from 'qrcode';

import { buildProfileQrUrl, PROFILE_QR_RENDER_OPTIONS, shareProfileQrLink } from '../../utils/profileQr';
import { getButtonColor, getButtonTextColor, getTextColor } from '../../utils/cssVariables';
import { getCurrentThemeName } from '../../theme';
import { RootState } from '../../store/store';

import './ProfileQrButton.css';

type ProfileQrButtonProps = {
  userId: string | number;
  isMentor: boolean;
  fileLabel?: string;
  profileName?: string;
  profileLastName?: string;
};

export const ProfileQrButton = ({
  userId,
  isMentor,
  profileName,
  profileLastName,
}: ProfileQrButtonProps) => {
  const { t } = useTranslation();
  const brandName = useSelector((state: RootState) => state.config.brandName);
  const displayBrand =
    getCurrentThemeName() === 'black'
      ? 'WOMEN.NYC'
      : brandName.trim() || 'WERULE';
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const profileUrl = buildProfileQrUrl(userId, isMentor, profileName, profileLastName);
  const clickLabel = t('mentor_profile.click_for_qr_code');
  const shareLabel = t('mentor_profile.tap_to_share_qr');

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(profileUrl, PROFILE_QR_RENDER_OPTIONS).then((dataUrl) => {
      if (!cancelled) {
        setQrDataUrl(dataUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, profileUrl]);

  const handleShare = async () => {
    const shared = await shareProfileQrLink(profileUrl);
    if (shared) {
      Message.success(t('mentor_profile.qr_link_shared'));
    } else {
      Message.error(t('mentor_profile.qr_link_share_failed'));
    }
  };

  return (
    <>
      <button
        type="button"
        className="profile-qr-share-row"
        aria-label={clickLabel}
        onClick={() => setOpen(true)}
      >
        <Typography.Title heading={5} className="mentor-profile__meta-line">
          <IconInfoCircle /> {clickLabel}
        </Typography.Title>
      </button>
      <Modal
        visible={open}
        title={t('mentor_profile.profile_qr_modal_title')}
        onCancel={() => setOpen(false)}
        footer={null}
        className="profile-qr-modal-dialog"
        wrapClassName="profile-qr-modal-wrapper"
      >
        <div className="profile-qr-modal">
          <Typography.Paragraph
            className="profile-qr-modal__subtitle"
            style={{ color: getTextColor(), margin: '0 0 16px' }}
          >
            {t('mentor_profile.profile_qr_modal_subtitle', { brandName: displayBrand })}
          </Typography.Paragraph>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Profile QR code" className="profile-qr-modal__image" />
          ) : null}
          <Button
            shape="round"
            size="large"
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
              marginTop: 16,
            }}
            onClick={handleShare}
          >
            {shareLabel}
          </Button>
        </div>
      </Modal>
    </>
  );
};
